/* ==========================================================================
   LIENZO 3D + REALIDAD AUMENTADA — Cuadros Splash

   Genera, en el propio navegador y sin ninguna libreria, un modelo 3D del
   lienzo a TAMAÑO REAL a partir de dos cosas que la tienda ya tiene:
     · la imagen limpia de la obra
     · la talla de la variante elegida ("60 x 30 cm")

   Por que escrito a mano y no con three.js:
   three.js + sus exportadores pesan ~900 KB. Un lienzo es una caja con una
   foto delante: la geometria son 24 vertices. Escribir el GLB y el USDZ
   directamente cuesta menos codigo que cargar la libreria, y sobre todo nos
   deja control total sobre el anclaje vertical del USDZ, que es justo lo que
   ninguna herramienta automatica hace bien (ver mas abajo).

   Ademas los bytes del JPEG se incrustan TAL CUAL, sin pasar por un canvas.
   Eso evita el cuello de botella de recodificar la textura (~1 s en movil) y
   de paso esquiva cualquier problema de "canvas tainting".

   -- LO CRITICO: QUE EL CUADRO SE CUELGUE EN LA PARED, NO EN EL SUELO --

   · Android: se usa el modo WebXR de <model-viewer>, que respeta
     ar-placement="wall". NO se usa Scene Viewer: es una app aparte y Android
     no le puede pasar un blob: generado por el navegador (falla o casca en
     silencio), asi que con geometria generada al vuelo queda descartado.

   · iOS: no existe WebXR en Safari, todo pasa por AR Quick Look. Y el USDZ
     que genera <model-viewer> por su cuenta SIEMPRE ancla al suelo, porque
     nunca escribe el anclaje vertical. Por eso generamos el USDZ nosotros,
     con el esquema oficial de Apple:
         apiSchemas = ["Preliminary_AnchoringAPI"]
         preliminary:anchoring:type = "plane"
         preliminary:planeAnchoring:alignment = "vertical"

   La escala real se garantiza porque en glTF y en USD la unidad es el METRO:
   la caja se construye midiendo lo que mide el cuadro de verdad.
   ========================================================================== */

(function (raiz) {
  'use strict';

  var PROFUNDIDAD_M = 0.035;   // grosor del bastidor: 3,5 cm de pino
  /* Ancho del liston visto por detras. Lo usan la geometria y la textura, y
     TIENEN que usar el mismo numero: si no, la madera pintada no cae encima
     de la madera modelada y el hueco sale con una cenefa de tela. */
  var ANCHO_LISTON_M = 0.04;

  /* ======================================================================
     0 · EL DORSO: UN BASTIDOR DE VERDAD

     El reverso no se sube como fichero: se dibuja aqui, en un canvas, igual
     que el resto del modelo se genera al vuelo. Un JPEG de bastidor por
     producto serian 1.712 ficheros mas que mantener, y todos practicamente
     iguales salvo por la proporcion.

     Lo que se pinta, de fuera hacia dentro, es lo que se ve al dar la vuelta
     a un lienzo montado:

       - la tela sobrante, doblada sobre el canto y grapada al liston
       - el liston de pino, con su veta
       - el hueco central, donde la tela se ve por detras, mas floja y en
         sombra porque esta hundida respecto al marco

     La textura se genera con la MISMA proporcion que el cuadro. Si se hiciera
     siempre cuadrada, al estirarla sobre un 120x40 el liston saldria cuatro
     veces mas gordo arriba que a los lados, y las grapas ovaladas.
     ====================================================================== */

  var PINO = {
    claro: '#e8d5b0',
    medio: '#dcc499',
    veta: 'rgba(150, 115, 70, 0.28)',
    canto: '#c9ad82'
  };
  var TELA = {
    fondo: '#f2ede3',
    sombra: 'rgba(120, 105, 85, 0.16)',
    trama: 'rgba(140, 125, 100, 0.10)'
  };

  function pintarVeta(ctx, x, y, an, al, horizontal) {
    /* Las vetas del pino son lineas largas y casi paralelas al liston, con
       alguna curva. Se dibujan con ondas de amplitud pequeña para que no
       parezcan rayas rectas de lapiz. */
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, an, al);
    ctx.clip();
    ctx.strokeStyle = PINO.veta;
    ctx.lineWidth = Math.max(1, Math.round(Math.min(an, al) * 0.035));
    var largo = horizontal ? an : al;
    var ancho = horizontal ? al : an;
    var n = Math.max(3, Math.round(ancho / 9));
    for (var i = 0; i < n; i++) {
      var t = (i + 0.5) / n;
      var base = (horizontal ? y : x) + t * ancho;
      var amp = ancho * 0.06 * (0.4 + ((i * 37) % 10) / 10);
      ctx.globalAlpha = 0.35 + ((i * 53) % 10) / 22;
      ctx.beginPath();
      for (var p = 0; p <= 12; p++) {
        var q = p / 12;
        var d = (horizontal ? x : y) + q * largo;
        var v = base + Math.sin(q * Math.PI * 2.4 + i) * amp;
        if (p === 0) ctx.moveTo(horizontal ? d : v, horizontal ? v : d);
        else ctx.lineTo(horizontal ? d : v, horizontal ? v : d);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  function pintarGrapa(ctx, cx, cy, largo, horizontal) {
    /* Una grapa vista por detras son dos patas clavadas y el lomo entre
       ellas. A la escala a la que se ve esto basta con el lomo y su sombra:
       un rectangulo metalico con una linea oscura debajo. */
    ctx.save();
    ctx.translate(cx, cy);
    if (!horizontal) ctx.rotate(Math.PI / 2);
    var gr = Math.max(1.5, largo * 0.26);
    ctx.fillStyle = 'rgba(80, 78, 74, 0.35)';
    ctx.fillRect(-largo / 2, -gr / 2 + gr * 0.5, largo, gr * 0.7);
    var g = ctx.createLinearGradient(0, -gr / 2, 0, gr / 2);
    g.addColorStop(0, '#b9b7b2');
    g.addColorStop(0.45, '#8e8c88');
    g.addColorStop(1, '#6f6d69');
    ctx.fillStyle = g;
    ctx.fillRect(-largo / 2, -gr / 2, largo, gr);
    ctx.restore();
  }

  function texturaBastidor(W, H) {
    var prop = W / H;
    var lado = 1024;
    var an = prop >= 1 ? lado : Math.round(lado * prop);
    var al = prop >= 1 ? Math.round(lado / prop) : lado;
    an = Math.max(256, an);
    al = Math.max(256, al);

    var c = document.createElement('canvas');
    c.width = an;
    c.height = al;
    var ctx = c.getContext('2d');

    // --- la tela del fondo, que es lo que se ve por el hueco ---
    ctx.fillStyle = TELA.fondo;
    ctx.fillRect(0, 0, an, al);
    ctx.strokeStyle = TELA.trama;
    ctx.lineWidth = 1;
    var paso = Math.max(3, Math.round(Math.min(an, al) / 190));
    for (var x = 0; x < an; x += paso) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, al); ctx.stroke();
    }
    for (var y = 0; y < al; y += paso) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(an, y); ctx.stroke();
    }

    /* El liston: un bastidor de 3,5 cm de canto lleva un marco de unos 4 cm
       de ancho por detras. Se calcula en proporcion al lado real del cuadro,
       no a la textura, para que un cuadro grande no salga con un marco
       gigante. */
    var anchoMarcoM = Math.min(ANCHO_LISTON_M, Math.min(W, H) * 0.22);
    var mx = Math.round((anchoMarcoM / W) * an);
    var my = Math.round((anchoMarcoM / H) * al);

    // sombra del hueco: la tela hundida se oscurece pegada al liston
    var s = ctx.createLinearGradient(0, my, 0, my + my * 0.9);
    s.addColorStop(0, TELA.sombra);
    s.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = s;
    ctx.fillRect(mx, my, an - mx * 2, my * 0.9);

    // --- el liston de pino, cuatro tramos ---
    ctx.fillStyle = PINO.medio;
    ctx.fillRect(0, 0, an, my);
    ctx.fillRect(0, al - my, an, my);
    ctx.fillRect(0, 0, mx, al);
    ctx.fillRect(an - mx, 0, mx, al);

    pintarVeta(ctx, 0, 0, an, my, true);
    pintarVeta(ctx, 0, al - my, an, my, true);
    pintarVeta(ctx, 0, 0, mx, al, false);
    pintarVeta(ctx, an - mx, 0, mx, al, false);

    // los ingletes de las esquinas, en diagonal
    ctx.strokeStyle = 'rgba(150, 120, 80, 0.45)';
    ctx.lineWidth = Math.max(1, Math.round(Math.min(mx, my) * 0.08));
    var esquinas = [[0, 0, mx, my], [an, 0, an - mx, my],
                    [0, al, mx, al - my], [an, al, an - mx, al - my]];
    esquinas.forEach(function (e) {
      ctx.beginPath(); ctx.moveTo(e[0], e[1]); ctx.lineTo(e[2], e[3]); ctx.stroke();
    });

    // borde interior del liston: canto vivo, un poco mas oscuro
    ctx.strokeStyle = PINO.canto;
    ctx.lineWidth = Math.max(1, Math.round(Math.min(mx, my) * 0.10));
    ctx.strokeRect(mx, my, an - mx * 2, al - my * 2);

    /* --- la tela doblada sobre el liston, con las grapas ---
       La tela sobrante no llega al borde interior: se dobla y se grapa a
       media madera, que es justo lo que deja ver la franja de pino. */
    var telaX = Math.round(mx * 0.52);
    var telaY = Math.round(my * 0.52);
    ctx.fillStyle = TELA.fondo;
    ctx.fillRect(0, 0, an, telaY);
    ctx.fillRect(0, al - telaY, an, telaY);
    ctx.fillRect(0, 0, telaX, al);
    ctx.fillRect(an - telaX, 0, telaX, al);

    // el doblez proyecta una sombra fina sobre la madera
    ctx.strokeStyle = 'rgba(110, 95, 70, 0.30)';
    ctx.lineWidth = Math.max(1, Math.round(Math.min(mx, my) * 0.07));
    ctx.strokeRect(telaX, telaY, an - telaX * 2, al - telaY * 2);

    /* Grapas: una cada 5 cm, que es como se tensa un lienzo de verdad.

       Van PERPENDICULARES al borde, no paralelas: la grapa atraviesa el
       doblez para sujetar la tela contra el liston. En los travesaños de
       arriba y abajo eso significa lomo vertical, y en los largueros de los
       lados, lomo horizontal — al reves de lo que parece a primera vista. */
    var pasoM = 0.05;
    var nH = Math.max(3, Math.round(W / pasoM));
    var nV = Math.max(3, Math.round(H / pasoM));
    var largoH = Math.max(4, Math.round((0.011 / H) * al));
    var largoV = Math.max(4, Math.round((0.011 / W) * an));
    var cyArriba = Math.round(telaY * 0.5);
    var cyAbajo = al - cyArriba;
    for (var i = 1; i < nH; i++) {
      var px = (i / nH) * an;
      pintarGrapa(ctx, px, cyArriba, largoH, false);
      pintarGrapa(ctx, px, cyAbajo, largoH, false);
    }
    var cxIzq = Math.round(telaX * 0.5);
    var cxDer = an - cxIzq;
    for (var j = 1; j < nV; j++) {
      var py = (j / nV) * al;
      pintarGrapa(ctx, cxIzq, py, largoV, true);
      pintarGrapa(ctx, cxDer, py, largoV, true);
    }

    return c;
  }

  /* Canvas -> bytes JPEG. Sincrono a proposito: en iOS el USDZ se arma dentro
     del propio manejador del clic y no cabe un await de por medio (ver la
     nota larga de la seccion 5). */
  function bytesDeCanvas(c) {
    var url = c.toDataURL('image/jpeg', 0.86);
    var b64 = url.slice(url.indexOf(',') + 1);
    var bruto = atob(b64);
    var out = new Uint8Array(bruto.length);
    for (var i = 0; i < bruto.length; i++) out[i] = bruto.charCodeAt(i);
    return out;
  }

  /* ======================================================================
     1 · TALLA -> METROS
     ====================================================================== */

  /* Acepta "60 x 30 cm", "60x30", "60 X 30 cm". Devuelve metros.
     Los formatos multi-pieza ("2 piezas de 60 x 60 cm") devuelven null a
     proposito: un modulo doble no es una caja, y es preferible no ofrecer el
     boton a mostrar algo que engañe sobre lo que se recibe. */
  function medirTalla(texto) {
    if (!texto) return null;
    var t = String(texto).trim();
    if (/pieza/i.test(t)) return null;

    var m = t.match(/^\s*(\d+(?:[.,]\d+)?)\s*[xX×]\s*(\d+(?:[.,]\d+)?)\s*(?:cm)?\s*$/);
    if (!m) return null;

    var ancho = parseFloat(m[1].replace(',', '.'));
    var alto = parseFloat(m[2].replace(',', '.'));
    if (!(ancho > 0) || !(alto > 0)) return null;
    if (ancho > 400 || alto > 400) return null; // fuera de rango creible

    return { ancho: ancho / 100, alto: alto / 100 };
  }

  /* ======================================================================
     2 · GEOMETRIA DE LA CAJA

     24 vertices (4 por cara) para que cada cara tenga sus propias UV.

     El canto no se pinta de un color plano: muestra la franja de la imagen
     que le corresponde, como en un lienzo montado con la obra estirada al
     borde. El ancho de esa franja se calcula en proporcion real: un canto de
     3 cm en un cuadro de 60 cm ocupa 3/60 de la imagen.
     ====================================================================== */

  function geometria(W, H, D) {
    var w = W / 2, h = H / 2, d = D / 2;

    // Franja del canto en coordenadas de textura (0-1), acotada por si el
    // cuadro fuera muy pequeño y el canto se comiera media obra.
    var fu = Math.min(0.08, D / W);
    var fv = Math.min(0.08, D / H);

    // En glTF el origen de la textura es la esquina SUPERIOR izquierda.
    var P = [], N = [], T = [];

    function cara(vs, normal, uvs) {
      for (var i = 0; i < 4; i++) {
        P.push(vs[i][0], vs[i][1], vs[i][2]);
        N.push(normal[0], normal[1], normal[2]);
        T.push(uvs[i][0], uvs[i][1]);
      }
    }

    // Frente (+Z): la obra completa.
    cara(
      [[-w, -h, d], [w, -h, d], [w, h, d], [-w, h, d]],
      [0, 0, 1],
      [[0, 1], [1, 1], [1, 0], [0, 0]]
    );
    // Canto derecho (+X): franja del borde derecho.
    cara(
      [[w, -h, d], [w, -h, -d], [w, h, -d], [w, h, d]],
      [1, 0, 0],
      [[1, 1], [1 - fu, 1], [1 - fu, 0], [1, 0]]
    );
    // Canto izquierdo (-X).
    cara(
      [[-w, -h, -d], [-w, -h, d], [-w, h, d], [-w, h, -d]],
      [-1, 0, 0],
      [[fu, 1], [0, 1], [0, 0], [fu, 0]]
    );
    // Canto superior (+Y).
    cara(
      [[-w, h, d], [w, h, d], [w, h, -d], [-w, h, -d]],
      [0, 1, 0],
      [[0, 0], [1, 0], [1, fv], [0, fv]]
    );
    // Canto inferior (-Y).
    cara(
      [[-w, -h, -d], [w, -h, -d], [w, -h, d], [-w, -h, d]],
      [0, -1, 0],
      [[0, 1 - fv], [1, 1 - fv], [1, 1], [0, 1]]
    );
    /* ---- EL DORSO, CON PROFUNDIDAD DE VERDAD ----

       Un bastidor no es una tapa plana: el liston sobresale y la tela queda
       hundida varios centimetros por dentro. Pintar esa sombra en la textura
       nunca convence, porque no se mueve al girar el cuadro.

       Asi que el hueco esta modelado: cuatro tramos de liston en el plano
       trasero, cuatro paredes que bajan hacia dentro, y el fondo de tela al
       fondo. Nueve caras en vez de una. La sombra la calcula el motor, y al
       girar el modelo el hueco se comporta como un hueco. */
    var mw = Math.min(ANCHO_LISTON_M, Math.min(W, H) * 0.22);
    var hp = Math.min(D * 0.72, 0.026);   // cuanto se hunde la tela
    var zb = -d;                          // cara trasera del liston
    var zf = -d + hp;                     // la tela, al fondo del hueco
    var xi = w - mw, yi = h - mw;         // borde interior del liston

    // Proyeccion plana para el dorso: se ve desde -Z, con la X invertida.
    function U(x) { return (w - x) / W; }
    function V(y) { return (y + h) / H; }

    // 1-4 · los cuatro tramos del liston, en el plano trasero
    cara([[w, yi, zb], [-w, yi, zb], [-w, h, zb], [w, h, zb]], [0, 0, -1],
         [[U(w), V(yi)], [U(-w), V(yi)], [U(-w), V(h)], [U(w), V(h)]]);
    cara([[w, -h, zb], [-w, -h, zb], [-w, -yi, zb], [w, -yi, zb]], [0, 0, -1],
         [[U(w), V(-h)], [U(-w), V(-h)], [U(-w), V(-yi)], [U(w), V(-yi)]]);
    cara([[-xi, -yi, zb], [-w, -yi, zb], [-w, yi, zb], [-xi, yi, zb]], [0, 0, -1],
         [[U(-xi), V(-yi)], [U(-w), V(-yi)], [U(-w), V(yi)], [U(-xi), V(yi)]]);
    cara([[w, -yi, zb], [xi, -yi, zb], [xi, yi, zb], [w, yi, zb]], [0, 0, -1],
         [[U(w), V(-yi)], [U(xi), V(-yi)], [U(xi), V(yi)], [U(w), V(yi)]]);

    /* 5-8 · las paredes del hueco. Son el canto interior del liston, o sea
       madera vista de perfil, asi que se mapean a media franja del marco de
       la textura en vez de a la tela. */
    /* Las paredes interiores deben leer la madera del perímetro, no la tela
       clara del centro. Esta franja coincide con la tela doblada que pinta
       texturaBastidor() y queda visible como zona de montaje y colgado. */
    var pv = mw / H * 0.5, pu = mw / W * 0.5;
    var franjaU = Math.min(0.12, (mw / W) * 0.52);
    var franjaV = Math.min(0.12, (mw / H) * 0.52);
    var carrilU = Math.min(0.16, (mw / W) * 0.90);
    var carrilV = Math.min(0.16, (mw / H) * 0.90);
    var maderaU0 = franjaU;
    var maderaU1 = carrilU;
    var maderaV0 = franjaV;
    var maderaV1 = carrilV;
    cara([[-xi, yi, zb], [xi, yi, zb], [xi, yi, zf], [-xi, yi, zf]], [0, -1, 0],
         [[maderaU0, maderaV0], [maderaU1, maderaV0], [maderaU1, maderaV1], [maderaU0, maderaV1]]);
    cara([[xi, -yi, zb], [-xi, -yi, zb], [-xi, -yi, zf], [xi, -yi, zf]], [0, 1, 0],
         [[maderaU1, 1 - maderaV0], [maderaU0, 1 - maderaV0], [maderaU0, 1 - maderaV1], [maderaU1, 1 - maderaV1]]);
    cara([[-xi, -yi, zb], [-xi, yi, zb], [-xi, yi, zf], [-xi, -yi, zf]], [1, 0, 0],
         [[1 - maderaU0, maderaV0], [1 - maderaU0, maderaV1], [1 - maderaU1, maderaV1], [1 - maderaU1, maderaV0]]);
    cara([[xi, yi, zb], [xi, -yi, zb], [xi, -yi, zf], [xi, yi, zf]], [-1, 0, 0],
         [[maderaU0, maderaV1], [maderaU0, maderaV0], [maderaU1, maderaV0], [maderaU1, maderaV1]]);

    // 9 · el fondo: la tela, vista por detras
    cara([[xi, -yi, zf], [-xi, -yi, zf], [-xi, yi, zf], [xi, yi, zf]], [0, 0, -1],
         [[U(xi), V(-yi)], [U(-xi), V(-yi)], [U(-xi), V(yi)], [U(xi), V(yi)]]);

    // Dos triangulos por cara, en sentido antihorario visto desde fuera.
    var CARAS = 14; // 1 frente + 4 cantos + 4 liston + 4 paredes + 1 fondo
    var I = [];
    for (var c = 0; c < CARAS; c++) {
      var b = c * 4;
      I.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }

    return {
      posiciones: P,
      normales: N,
      uvs: T,
      indices: I,
      vertices: CARAS * 4,
      caras: CARAS,
      min: [-w, -h, -d],
      max: [w, h, d],
      indicesObra: 30,                 // caras 0-4: frente y cantos
      indicesDorso: (CARAS - 5) * 6    // caras 5-13: todo el bastidor
    };
  }

  /* ======================================================================
     3 · CONSTRUCTOR DE GLB (visor 3D y AR de Android)
     ====================================================================== */

  function relleno4(n) {
    return (4 - (n % 4)) % 4;
  }

  /* `relieve` son los bytes JPEG del mapa de normales (o null) y `fuerza` es
     cuanto se nota, de 0 a 2. Con fuerza 0 o sin mapa, el modelo sale
     exactamente igual que antes: ni un byte de mas. */
  function construirGLB(W, H, D, imagen, mime, relieve, fuerza) {
    var conRelieve = !!(relieve && relieve.byteLength && fuerza > 0);
    var g = geometria(W, H, D);
    var dorso = bytesDeCanvas(texturaBastidor(W, H));

    var idx = new Uint16Array(g.indices);
    var pos = new Float32Array(g.posiciones);
    var nor = new Float32Array(g.normales);
    var uvs = new Float32Array(g.uvs);

    var oIdx = 0;
    var oPos = idx.byteLength + relleno4(idx.byteLength);
    var oNor = oPos + pos.byteLength;
    var oUv = oNor + nor.byteLength;
    var oImg = oUv + uvs.byteLength;
    oImg += relleno4(oImg);
    var oDorso = oImg + imagen.byteLength;
    oDorso += relleno4(oDorso);
    var oRel = oDorso + dorso.byteLength;
    oRel += relleno4(oRel);
    var totalBin = conRelieve ? oRel + relieve.byteLength : oDorso + dorso.byteLength;
    totalBin += relleno4(totalBin);

    var bin = new Uint8Array(totalBin);
    bin.set(new Uint8Array(idx.buffer, idx.byteOffset, idx.byteLength), oIdx);
    bin.set(new Uint8Array(pos.buffer, pos.byteOffset, pos.byteLength), oPos);
    bin.set(new Uint8Array(nor.buffer, nor.byteOffset, nor.byteLength), oNor);
    bin.set(new Uint8Array(uvs.buffer, uvs.byteOffset, uvs.byteLength), oUv);
    bin.set(imagen, oImg);
    bin.set(dorso, oDorso);
    if (conRelieve) bin.set(relieve, oRel);

    var json = {
      asset: { version: '2.0', generator: 'Cuadros Splash canvas-3d' },
      scene: 0,
      scenes: [{ nodes: [0] }],
      nodes: [{ mesh: 0, name: 'Lienzo' }],
      meshes: [
        {
          name: 'Lienzo',
          primitives: [
            {
              attributes: { POSITION: 1, NORMAL: 2, TEXCOORD_0: 3 },
              indices: 0,
              material: 0
            },
            {
              // El dorso lleva su propia textura de bastidor, asi que si
              // necesita TEXCOORD_0: las UV de esa cara van de (0,0) a (1,1)
              // y la textura ya se genera con la proporcion del cuadro.
              attributes: { POSITION: 1, NORMAL: 2, TEXCOORD_0: 3 },
              indices: 4,
              material: 1
            }
          ]
        }
      ],
      accessors: [
        {
          bufferView: 0,
          byteOffset: 0,
          componentType: 5123,
          count: g.indicesObra,
          type: 'SCALAR'
        },
        {
          bufferView: 1,
          componentType: 5126,
          count: g.vertices,
          type: 'VEC3',
          min: g.min,
          max: g.max
        },
        { bufferView: 2, componentType: 5126, count: g.vertices, type: 'VEC3' },
        { bufferView: 3, componentType: 5126, count: g.vertices, type: 'VEC2' },
        {
          bufferView: 0,
          byteOffset: g.indicesObra * 2,
          componentType: 5123,
          count: g.indicesDorso,
          type: 'SCALAR'
        }
      ],
      bufferViews: [
        { buffer: 0, byteOffset: oIdx, byteLength: idx.byteLength, target: 34963 },
        { buffer: 0, byteOffset: oPos, byteLength: pos.byteLength, target: 34962 },
        { buffer: 0, byteOffset: oNor, byteLength: nor.byteLength, target: 34962 },
        { buffer: 0, byteOffset: oUv, byteLength: uvs.byteLength, target: 34962 },
        { buffer: 0, byteOffset: oImg, byteLength: imagen.byteLength },
        { buffer: 0, byteOffset: oDorso, byteLength: dorso.byteLength }
      ].concat(conRelieve
        ? [{ buffer: 0, byteOffset: oRel, byteLength: relieve.byteLength }]
        : []),
      buffers: [{ byteLength: totalBin }],
      images: [
        { bufferView: 4, mimeType: mime },
        { bufferView: 5, mimeType: 'image/jpeg' } // el bastidor
      ].concat(conRelieve
        ? [{ bufferView: 6, mimeType: 'image/jpeg' }] // el relieve del gel
        : []),
      // CLAMP_TO_EDGE en los dos ejes: sin esto, la franja del canto podria
      // repetir la imagen y aparecerian costuras en los bordes.
      //
      // Los filtros se dejan SIN declarar a proposito. Las obras vienen en
      // tamaños arbitrarios (830x419, 1000x1000...), casi nunca potencias de
      // dos, y exigir mipmaps sobre una textura asi es invalido en WebGL1:
      // la textura puede salir negra. Sin declararlos, cada visor elige el
      // mejor filtro que soporte, con mipmaps donde pueda.
      samplers: [{ wrapS: 33071, wrapT: 33071 }],
      textures: [
        { sampler: 0, source: 0 },
        { sampler: 0, source: 1 }
      ].concat(conRelieve ? [{ sampler: 0, source: 2 }] : []),
      materials: [
        {
          name: 'obra',
          pbrMetallicRoughness: {
            baseColorTexture: { index: 0 },
            metallicFactor: 0,
            /* LA RUGOSIDAD BAJA CUANDO SUBE EL GEL, Y NO ES UN CAPRICHO.
               Un relieve solo se VE si la luz rebota en el. Sobre una
               superficie perfectamente mate (0,85) un mapa de normales apenas
               produce un sombreado difuso: puedes subir el relieve al triple
               y seguir sin notarlo, que es justo lo que le pasaba a Gabriel.
               Y ademas es lo cierto: el gel es transparente y algo satinado,
               brilla MAS que el lienzo crudo. Asi que a mas gel, menos mate.
               Sin gel (fuerza 0) se queda en 0,85, el lienzo desnudo. */
            roughnessFactor: conRelieve
              ? Math.max(0.55, 0.85 - fuerza * 0.11)
              : 0.85
          },
          /* El relieve del gel. `scale` es cuanto se nota, y sale del
             deslizador del editor: 0 deja el lienzo liso como antes. */
          normalTexture: conRelieve ? { index: 2, scale: fuerza } : undefined
        },
        {
          name: 'bastidor',
          pbrMetallicRoughness: {
            baseColorTexture: { index: 1 },
            metallicFactor: 0,
            // la madera en bruto y la tela cruda reflejan aun menos que la
            // cara impresa: sin esto el dorso brilla como un plastico
            roughnessFactor: 0.95
          }
        }
      ]
    };

    var textoJson = JSON.stringify(json);
    var bytesJson = new TextEncoder().encode(textoJson);
    var padJson = relleno4(bytesJson.length);

    var lenJson = bytesJson.length + padJson;
    var total = 12 + 8 + lenJson + 8 + totalBin;

    var buf = new ArrayBuffer(total);
    var dv = new DataView(buf);
    var u8 = new Uint8Array(buf);
    var o = 0;

    dv.setUint32(o, 0x46546c67, true); o += 4; // "glTF"
    dv.setUint32(o, 2, true); o += 4;
    dv.setUint32(o, total, true); o += 4;

    dv.setUint32(o, lenJson, true); o += 4;
    dv.setUint32(o, 0x4e4f534a, true); o += 4; // "JSON"
    u8.set(bytesJson, o); o += bytesJson.length;
    for (var i = 0; i < padJson; i++) u8[o++] = 0x20; // espacios

    dv.setUint32(o, totalBin, true); o += 4;
    dv.setUint32(o, 0x004e4942, true); o += 4; // "BIN"
    u8.set(bin, o);

    return u8;
  }

  /* ======================================================================
     4 · CONSTRUCTOR DE USDZ (AR Quick Look de iOS)
     ====================================================================== */

  /* Quick Look reorienta el modelo al anclarlo en vertical: trata el plano
     de la pared como si fuera el suelo, asi que el eje que sale de la pared
     es +Y. Un cuadro mira hacia +Z, de modo que hay que girarlo -90 grados
     sobre X para que su cara quede mirando hacia +Y, es decir, hacia fuera
     de la pared. Si en pruebas sobre iPhone apareciera boca abajo o mirando
     hacia dentro, este es el numero que hay que tocar (90 / -90). */
  var GIRO_PARED_X = -90;

  function num(n) {
    // USD no admite notacion exponencial: 6 decimales bastan al milimetro.
    var s = n.toFixed(6);
    return s.replace(/\.?0+$/, '') || '0';
  }

  /* ======================================================================
     SALA DE MUSEO

     model-viewer no admite focos sueltos: toda la luz le entra por una imagen
     de entorno equirectangular (una panoramica 360 de la sala imaginaria en
     la que esta el objeto). Asi que la sala se dibuja, y de ahi sale la luz.

     Lo que se pinta es una sala oscura con dos proyectores calidos arriba, a
     los lados, apuntando al cuadro, y un rebote frio y flojo desde abajo para
     que el dorso no se quede en negro absoluto. Esa diferencia entre el foco
     calido de arriba y la penumbra de abajo es lo que da el relieve: sin ella
     la obra se ve plana aunque el modelo tenga profundidad.

     Es la misma iluminacion de una sala de exposicion: fondo apagado, luz
     concentrada en la pieza.
     ====================================================================== */

  var _sala = null;

  function entornoSala() {
    if (_sala) return _sala;

    // 2:1 es la proporcion obligada de una equirectangular
    var an = 512, al = 256;
    var c = document.createElement('canvas');
    c.width = an;
    c.height = al;
    var ctx = c.getContext('2d');

    /* El ambiente.

       OJO CON LA TENTACION DE PINTARLO OSCURO: esta imagen no es el fondo que
       se ve, es la UNICA fuente de luz del modelo. model-viewer suma todo el
       entorno para iluminar, asi que una sala pintada de negro deja la obra
       marron y sucia, por muchos focos que se le pongan. La primera version
       de esto tenia el fondo casi negro y el cuadro no se veia.

       El drama de sala de museo lo pone el fondo del visor, que es aparte
       (cs3d-capa en canvas-3d.css). Aqui lo que interesa es luz: pared clara
       arriba, suelo en penumbra, y los focos por encima de eso. */
    var base = ctx.createLinearGradient(0, 0, 0, al);
    /* SUBIDA LA PARTE DE ABAJO EL 20 SEP 2026. Gabriel: "no quiero que sea
       tan oscura". Y no era manía: esta imagen es la UNICA fuente de luz del
       modelo, y la mitad de abajo estaba en penumbra (#5d5852), asi que el
       cuadro recibia luz por arriba y casi nada por abajo.
       Se levanta el suelo y la pared baja y se deja el techo casi igual, para
       no quemar los claros. Calculado sobre la esfera entera, pesando cada
       fila por su angulo solido (las de en medio abarcan mucho mas cielo que
       el cenit o el nadir):
         luz media  183,7 -> 215,3   (+17 %)
         suelo       89   -> 150     (+69 %)
         pared baja 149   -> 201     (+35 %)
         techo      247   -> 250     (+1 %)
       Si aun asi se viera oscuro, lo siguiente NO es subir mas esto —se
       aplanaria el modelado— sino el `exposure` del visor, que hoy esta en
       1,15. */
    /* LUZ BLANCA DE MUSEO (20 sep 2026). Gabriel: "luz blanca de museo, no
       tan oscuro y marron". Y tenia razon en lo del marron: estos tonos eran
       beige (calidez R-B de +17), asi que TODO cuadro salia con un velo
       calido encima — en una tienda de reproducciones, eso es falsear el
       producto igual que lo hacia el tone-mapping de cine.
       Ahora son grises neutros, un pelin frios, que es la luz de una sala de
       exposicion de verdad. Medido sobre la esfera, pesando cada fila por su
       angulo solido:
         luz media  215,3 -> 230,0   (+7 %)
         calidez    +16,6 -> -5,1    (de beige a blanco neutro)
         suelo       150  -> 180     (+20 %)
         techo       250  -> 255     (+2 %)
       El degradado SE QUEDA, no se aplana: sin diferencia entre arriba y
       abajo no hay modelado y el lienzo parece una calcomania. Lo que cambia
       es el color de la luz y cuanto llega abajo. */
    base.addColorStop(0, '#ffffff');
    base.addColorStop(0.38, '#f2f4f6');
    base.addColorStop(0.66, '#d8dcdf');
    base.addColorStop(1, '#b0b5b9');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, an, al);

    /* Los focos. En una equirectangular la altura es la inclinacion: y=0 es
       el cenit. Se colocan a un tercio de altura, que es de donde alumbra un
       raíl de museo, y repartidos para que uno entre por delante-izquierda y
       otro por delante-derecha. */
    function foco(cx, cy, r, fuerza, calido) {
      var g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
      /* Los focos tambien eran ambar (255,226,178 en el segundo tramo es casi
         color bombilla vieja). En una sala de museo los focos son blancos:
         lo que los distingue es de DONDE vienen, no de que color son. Se
         dejan apenas insinuados, uno un puntito calido y otro un puntito
         frio, para que el modelado no quede plano. */
      g.addColorStop(0, calido
        ? 'rgba(255, 252, 246, ' + fuerza + ')'
        : 'rgba(240, 246, 255, ' + fuerza + ')');
      g.addColorStop(0.45, calido
        ? 'rgba(255, 248, 236, ' + (fuerza * 0.34) + ')'
        : 'rgba(228, 238, 252, ' + (fuerza * 0.3) + ')');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    }

    foco(an * 0.28, al * 0.17, al * 0.72, 1, true);    // proyector principal
    foco(an * 0.72, al * 0.22, al * 0.55, 0.78, true);  // el de relleno
    foco(an * 0.50, al * 0.04, al * 0.42, 0.60, true);  // cenital
    foco(an * 0.02, al * 0.20, al * 0.40, 0.55, true);  // el que da la vuelta
    foco(an * 0.98, al * 0.20, al * 0.40, 0.55, true);  // (la panoramica cierra)
    foco(an * 0.50, al * 0.92, al * 0.50, 0.20, false); // rebote frio del suelo

    _sala = c.toDataURL('image/jpeg', 0.9);
    return _sala;
  }

  function relleno(n, valor) {
    // "4, 4, 4, ..." tantas veces como caras tenga la malla
    var a = [];
    for (var i = 0; i < n; i++) a.push(valor);
    return a.join(', ');
  }

  function carasDorso(total) {
    // Las cinco primeras caras son la obra (frente y cantos); el resto,
    // bastidor. Se listan para el GeomSubset que les da el otro material.
    var a = [];
    for (var i = 5; i < total; i++) a.push(i);
    return a.join(', ');
  }

  function construirUSDA(W, H, D, nombreTextura) {
    var g = geometria(W, H, D);

    var puntos = [], normales = [], sts = [];
    for (var i = 0; i < g.vertices; i++) {
      puntos.push(
        '(' + num(g.posiciones[i * 3]) + ', ' + num(g.posiciones[i * 3 + 1]) + ', ' + num(g.posiciones[i * 3 + 2]) + ')'
      );
      normales.push(
        '(' + num(g.normales[i * 3]) + ', ' + num(g.normales[i * 3 + 1]) + ', ' + num(g.normales[i * 3 + 2]) + ')'
      );
      // USD pone el origen de la textura ABAJO a la izquierda; glTF arriba.
      sts.push('(' + num(g.uvs[i * 2]) + ', ' + num(1 - g.uvs[i * 2 + 1]) + ')');
    }

    var caras = [];
    for (var c = 0; c < g.caras; c++) {
      var b = c * 4;
      caras.push(b, b + 1, b + 2, b + 3);
    }

    return [
      '#usda 1.0',
      '(',
      '    defaultPrim = "Cuadro"',
      '    metersPerUnit = 1',
      '    upAxis = "Y"',
      ')',
      '',
      'def Xform "Cuadro" (',
      '    apiSchemas = ["Preliminary_AnchoringAPI"]',
      ')',
      '{',
      '    token preliminary:anchoring:type = "plane"',
      '    token preliminary:planeAnchoring:alignment = "vertical"',
      '    double3 xformOp:rotateXYZ = (' + GIRO_PARED_X + ', 0, 0)',
      '    uniform token[] xformOpOrder = ["xformOp:rotateXYZ"]',
      '',
      '    def Mesh "Lienzo"',
      '    {',
      '        uniform bool doubleSided = 0',
      '        int[] faceVertexCounts = [' + relleno(g.caras, 4) + ']',
      '        int[] faceVertexIndices = [' + caras.join(', ') + ']',
      '        point3f[] points = [' + puntos.join(', ') + ']',
      '        normal3f[] primvars:normals = [' + normales.join(', ') + '] (',
      '            interpolation = "vertex"',
      '        )',
      '        texCoord2f[] primvars:st = [' + sts.join(', ') + '] (',
      '            interpolation = "vertex"',
      '        )',
      '        rel material:binding = </Cuadro/Obra>',
      '',
      /* La cara 5 es el dorso y lleva otro material. En USD eso se dice con
         un GeomSubset de la familia "materialBind": el resto de caras se
         quedan con el binding del Mesh. */
      '        def GeomSubset "Dorso"',
      '        {',
      '            uniform token elementType = "face"',
      '            uniform token familyName = "materialBind"',
      '            int[] indices = [' + carasDorso(g.caras) + ']',
      '            rel material:binding = </Cuadro/Bastidor>',
      '        }',
      '    }',
      '',
      '    def Material "Bastidor"',
      '    {',
      '        token outputs:surface.connect = </Cuadro/Bastidor/Superficie.outputs:surface>',
      '',
      '        def Shader "Superficie"',
      '        {',
      '            uniform token info:id = "UsdPreviewSurface"',
      '            color3f inputs:diffuseColor.connect = </Cuadro/Bastidor/Textura.outputs:rgb>',
      '            float inputs:metallic = 0',
      '            float inputs:roughness = 0.95',
      '            float inputs:opacity = 1',
      '            token outputs:surface',
      '        }',
      '',
      '        def Shader "Textura"',
      '        {',
      '            uniform token info:id = "UsdUVTexture"',
      '            asset inputs:file = @bastidor.jpg@',
      '            float2 inputs:st.connect = </Cuadro/Bastidor/LectorUV.outputs:result>',
      '            token inputs:wrapS = "clamp"',
      '            token inputs:wrapT = "clamp"',
      '            float3 outputs:rgb',
      '        }',
      '',
      '        def Shader "LectorUV"',
      '        {',
      '            uniform token info:id = "UsdPrimvarReader_float2"',
      '            token inputs:varname = "st"',
      '            float2 outputs:result',
      '        }',
      '    }',
      '',
      '    def Material "Obra"',
      '    {',
      '        token outputs:surface.connect = </Cuadro/Obra/Superficie.outputs:surface>',
      '',
      '        def Shader "Superficie"',
      '        {',
      '            uniform token info:id = "UsdPreviewSurface"',
      '            color3f inputs:diffuseColor.connect = </Cuadro/Obra/Textura.outputs:rgb>',
      '            float inputs:metallic = 0',
      '            float inputs:roughness = 0.85',
      '            float inputs:opacity = 1',
      '            token outputs:surface',
      '        }',
      '',
      '        def Shader "Textura"',
      '        {',
      '            uniform token info:id = "UsdUVTexture"',
      '            asset inputs:file = @' + nombreTextura + '@',
      '            float2 inputs:st.connect = </Cuadro/Obra/LectorUV.outputs:result>',
      '            token inputs:wrapS = "clamp"',
      '            token inputs:wrapT = "clamp"',
      '            float3 outputs:rgb',
      '        }',
      '',
      '        def Shader "LectorUV"',
      '        {',
      '            uniform token info:id = "UsdPrimvarReader_float2"',
      '            token inputs:varname = "st"',
      '            float2 outputs:result',
      '        }',
      '    }',
      '}',
      ''
    ].join('\n');
  }

  /* --- CRC32, que el ZIP exige por fichero --- */
  var TABLA_CRC = null;
  function crc32(datos) {
    if (!TABLA_CRC) {
      TABLA_CRC = new Uint32Array(256);
      for (var n = 0; n < 256; n++) {
        var c = n;
        for (var k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        TABLA_CRC[n] = c >>> 0;
      }
    }
    var crc = 0xffffffff;
    for (var i = 0; i < datos.length; i++) {
      crc = TABLA_CRC[(crc ^ datos[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
  }

  /* Un USDZ es un ZIP SIN COMPRIMIR en el que los datos de cada fichero
     tienen que empezar en un multiplo de 64 bytes. El relleno se mete en el
     campo "extra" de la cabecera local, que necesita 4 bytes de cabecera
     propia: si hicieran falta menos de 4, se añaden otros 64. */
  function empaquetarUSDZ(ficheros) {
    var codificador = new TextEncoder();
    var partes = [];
    var central = [];
    var desplazamiento = 0;

    ficheros.forEach(function (f) {
      var nombre = codificador.encode(f.nombre);
      var inicioDatos = desplazamiento + 30 + nombre.length;
      var extra = (64 - (inicioDatos % 64)) % 64;
      if (extra > 0 && extra < 4) extra += 64;

      var crc = crc32(f.datos);
      var cabecera = new Uint8Array(30 + nombre.length + extra);
      var dv = new DataView(cabecera.buffer);
      dv.setUint32(0, 0x04034b50, true);
      dv.setUint16(4, 20, true);   // version necesaria
      dv.setUint16(6, 0, true);    // banderas
      dv.setUint16(8, 0, true);    // metodo 0 = sin comprimir
      dv.setUint16(10, 0, true);   // hora
      dv.setUint16(12, 0, true);   // fecha
      dv.setUint32(14, crc, true);
      dv.setUint32(18, f.datos.length, true);
      dv.setUint32(22, f.datos.length, true);
      dv.setUint16(26, nombre.length, true);
      dv.setUint16(28, extra, true);
      cabecera.set(nombre, 30);
      if (extra >= 4) {
        var dvx = new DataView(cabecera.buffer, 30 + nombre.length);
        dvx.setUint16(0, 0x1986, true);     // id propio del campo extra
        dvx.setUint16(2, extra - 4, true);  // longitud util
      }

      partes.push(cabecera, f.datos);
      central.push({ nombre: nombre, crc: crc, tam: f.datos.length, off: desplazamiento });
      desplazamiento += cabecera.length + f.datos.length;
    });

    var inicioCentral = desplazamiento;
    central.forEach(function (e) {
      var reg = new Uint8Array(46 + e.nombre.length);
      var dv = new DataView(reg.buffer);
      dv.setUint32(0, 0x02014b50, true);
      dv.setUint16(4, 20, true);
      dv.setUint16(6, 20, true);
      dv.setUint16(8, 0, true);
      dv.setUint16(10, 0, true);
      dv.setUint16(12, 0, true);
      dv.setUint16(14, 0, true);
      dv.setUint32(16, e.crc, true);
      dv.setUint32(20, e.tam, true);
      dv.setUint32(24, e.tam, true);
      dv.setUint16(28, e.nombre.length, true);
      dv.setUint16(30, 0, true);
      dv.setUint16(32, 0, true);
      dv.setUint16(34, 0, true);
      dv.setUint16(36, 0, true);
      dv.setUint32(38, 0, true);
      dv.setUint32(42, e.off, true);
      reg.set(e.nombre, 46);
      partes.push(reg);
      desplazamiento += reg.length;
    });

    var fin = new Uint8Array(22);
    var dvf = new DataView(fin.buffer);
    dvf.setUint32(0, 0x06054b50, true);
    dvf.setUint16(8, central.length, true);
    dvf.setUint16(10, central.length, true);
    dvf.setUint32(12, desplazamiento - inicioCentral, true);
    dvf.setUint32(16, inicioCentral, true);
    partes.push(fin);

    var total = partes.reduce(function (s, p) { return s + p.length; }, 0);
    var salida = new Uint8Array(total);
    var o = 0;
    partes.forEach(function (p) { salida.set(p, o); o += p.length; });
    return salida;
  }

  function construirUSDZ(W, H, D, imagen, mime) {
    var nombreTextura = mime === 'image/png' ? 'obra.png' : 'obra.jpg';
    var usda = construirUSDA(W, H, D, nombreTextura);
    var dorso = bytesDeCanvas(texturaBastidor(W, H));
    // El .usda va PRIMERO: Quick Look abre el primer fichero del paquete.
    return empaquetarUSDZ([
      { nombre: 'modelo.usda', datos: new TextEncoder().encode(usda) },
      { nombre: nombreTextura, datos: imagen },
      { nombre: 'bastidor.jpg', datos: dorso }
    ]);
  }

  /* ======================================================================
     5 * INTERFAZ

     El flujo esta pensado alrededor de una restriccion que no se ve en el
     codigo pero decide el diseno: WebXR (el AR de Android) exige que la
     sesion arranque desde un gesto REAL del usuario. Si al pulsar el boton
     nos ponemos a descargar la imagen o a cargar model-viewer, el gesto
     caduca y el AR falla en silencio.

     Por eso:
       - Los bytes de la obra se descargan ANTES, en cuanto el usuario roza
         la zona de los botones.
       - En iOS el USDZ se arma dentro del propio manejador del clic (es
         instantaneo: solo se pegan bytes) y se pincha el enlace acto
         seguido, sin ningun await de por medio.
       - En Android no se lanza el AR a pelo: se abre el visor 3D y se deja
         que sea el boton de AR de model-viewer el que arranque la sesion.
         Ese clic es un gesto nuevo y limpio.
     ====================================================================== */

  var URL_MODEL_VIEWER =
    'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';

  function esIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  function esAndroid() {
    return /Android/.test(navigator.userAgent);
  }

  /* Lee la talla elegida mirando TODA la seccion.
     En Dawn el selector de variantes vive fuera del <form>, asi que buscarlo
     dentro del formulario no encuentra nada (trampa ya documentada). */
  function tallaElegida(seccion) {
    var textos = [];
    seccion.querySelectorAll('input[type="radio"]:checked').forEach(function (i) {
      textos.push(i.value || '');
      var lbl = i.labels && i.labels[0];
      if (lbl) textos.push(lbl.textContent || '');
    });
    seccion.querySelectorAll('select').forEach(function (sel) {
      var opt = sel.options[sel.selectedIndex];
      if (opt) textos.push(opt.value || opt.textContent || '');
    });
    for (var i = 0; i < textos.length; i++) {
      var m = medirTalla(textos[i]);
      if (m) return m;
    }
    return null;
  }

  function cargarModelViewer() {
    if (window.__cs3dMV) return window.__cs3dMV;
    window.__cs3dMV = new Promise(function (res, rej) {
      if (customElements.get('model-viewer')) return res();
      var s = document.createElement('script');
      s.type = 'module';
      s.src = URL_MODEL_VIEWER;
      s.onload = function () { res(); };
      s.onerror = function () { rej(new Error('no se pudo cargar el visor 3D')); };
      document.head.appendChild(s);
    });
    return window.__cs3dMV;
  }

  /* Descarga los bytes de la obra. No se pasa por un canvas a proposito: asi
     la textura no se recodifica (que es lo lento, ~1 s en movil) y de paso no
     hay riesgo de canvas contaminado. Si llegara un formato que ni glTF ni
     USDZ admiten (webp), ahi si toca recodificar a JPEG. */
  /* EL RELIEVE DEL GEL (19 sep 2026).

     El problema que resuelve: hasta hoy el lienzo era una PLANCHA LISA. El
     material iba con `roughness 0.85` y ningun mapa de normales, asi que la
     luz resbalaba por encima sin encontrar nada. Y resulta que Cuadros Splash
     vende como extra premium justo eso: pinceladas de gel transparente
     aplicadas a mano sobre la impresion, de 27 a 189 euros. El visor 3D no lo
     ensenaba en absoluto.

     COMO SE SACA EL RELIEVE SIN TENER NINGUN DATO DE RELIEVE. No hay un
     escaneo del gel; lo unico que hay es la imagen de la obra. Pero el gel se
     aplica SIGUIENDO la pintura, asi que la propia obra es un mapa de altura
     bastante fiel: donde hay empaste y luz, hay gel. Se toma la luminancia
     como altura, se suaviza un poco (si no, se amplifica el ruido del JPEG y
     el cuadro sale con grano metalico), y se deriva la pendiente en x e y.
     Esa pendiente ES la normal.

     LA INTENSIDAD NO VA AQUI. Aqui se fija una pendiente base y el mando fino
     es `normalTexture.scale` en el glTF, que sale a un deslizador del editor
     de Shopify. Asi Gabriel lo ajusta mirandolo, que es la unica forma de
     acertar con algo que se ve.

     1280 px de lado basta: el relieve es de baja frecuencia comparado con el
     color, y un mapa mas grande solo engorda el modelo. */
  function mapaRelieve(url) {
    return new Promise(function (res, rej) {
      var img = new Image();
      img.crossOrigin = 'anonymous'; // ANTES del src, como en recodificarJPEG
      img.onload = function () {
        try {
          /* MAS PEQUENO EN EL MOVIL, Y NO ES TACANERIA. Sacar el relieve es
             trabajo sincrono en el hilo principal: un suavizado de 9 muestras
             por pixel mas la derivada. A 1280x1067 son unos 12 millones de
             operaciones, que en un telefono modesto son medio segundo con la
             pantalla congelada — justo lo que llevamos todo el dia quitando.
             A 768 el trabajo baja al 36% y el relieve no se nota menos: es de
             baja frecuencia comparado con el color. */
          var techo = (window.innerWidth || 1024) < 700 ? 768 : 1280;
          var largo = Math.max(img.naturalWidth, img.naturalHeight) || 1;
          var esc = Math.min(techo, largo) / largo;
          var w = Math.max(2, Math.round(img.naturalWidth * esc));
          var h = Math.max(2, Math.round(img.naturalHeight * esc));
          var c = document.createElement('canvas');
          c.width = w; c.height = h;
          var ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          var d = ctx.getImageData(0, 0, w, h).data;

          var altura = new Float32Array(w * h);
          for (var i = 0, q = 0; q < altura.length; i += 4, q++) {
            altura[q] = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
          }

          /* Suavizado 3x3. Sin esto, el ruido de compresion del JPEG se
             convierte en pendiente y el cuadro brilla como una lija. */
          var suave = new Float32Array(w * h);
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              var s = 0, n = 0;
              for (var dy = -1; dy <= 1; dy++) {
                var yy = y + dy; if (yy < 0 || yy >= h) continue;
                for (var dx = -1; dx <= 1; dx++) {
                  var xx = x + dx; if (xx < 0 || xx >= w) continue;
                  s += altura[yy * w + xx]; n++;
                }
              }
              suave[y * w + x] = s / n;
            }
          }

          var salida = ctx.createImageData(w, h);
          var o = salida.data;
          var K = 6; /* pendiente base; el mando fino es normalTexture.scale */
          for (var y2 = 0; y2 < h; y2++) {
            for (var x2 = 0; x2 < w; x2++) {
              var xi = x2 > 0 ? x2 - 1 : 0;
              var xd = x2 < w - 1 ? x2 + 1 : w - 1;
              var ya = y2 > 0 ? y2 - 1 : 0;
              var yb = y2 < h - 1 ? y2 + 1 : h - 1;
              var gx = (suave[y2 * w + xd] - suave[y2 * w + xi]) * K;
              var gy = (suave[yb * w + x2] - suave[ya * w + x2]) * K;
              /* Convenio de glTF: normal en espacio tangente, verde = +Y
                 hacia ARRIBA. En una imagen la y crece hacia abajo, de ahi
                 el cambio de signo. Si las pinceladas se vieran HUNDIDAS en
                 vez de en relieve, es este signo y solo este. */
              var nx = -gx, ny = -gy;
              var len = Math.sqrt(nx * nx + ny * ny + 1) || 1;
              var k = (y2 * w + x2) * 4;
              o[k]     = Math.round((nx / len * 0.5 + 0.5) * 255);
              o[k + 1] = Math.round((ny / len * 0.5 + 0.5) * 255);
              o[k + 2] = Math.round((1 / len * 0.5 + 0.5) * 255);
              o[k + 3] = 255;
            }
          }
          ctx.putImageData(salida, 0, 0);
          c.toBlob(function (blob) {
            if (!blob) return rej(new Error('no se pudo crear el relieve'));
            blob.arrayBuffer().then(function (b) { res(new Uint8Array(b)); });
          }, 'image/jpeg', 0.92);
        } catch (e) { rej(e); }
      };
      img.onerror = function () { rej(new Error('no se pudo leer la obra')); };
      img.src = url;
    });
  }

  function traerObra(url) {
    return fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.arrayBuffer();
      })
      .then(function (buf) {
        var b = new Uint8Array(buf);
        if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) {
          return { datos: b, mime: 'image/jpeg' };
        }
        if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
          return { datos: b, mime: 'image/png' };
        }
        return recodificarJPEG(url);
      });
  }

  function recodificarJPEG(url) {
    return new Promise(function (res, rej) {
      var img = new Image();
      img.crossOrigin = 'anonymous'; // ANTES de asignar src, o Safari lo ignora
      img.onload = function () {
        var lienzo = document.createElement('canvas');
        var lado = Math.min(2048, Math.max(img.naturalWidth, img.naturalHeight));
        var escala = lado / Math.max(img.naturalWidth, img.naturalHeight);
        lienzo.width = Math.round(img.naturalWidth * escala);
        lienzo.height = Math.round(img.naturalHeight * escala);
        lienzo.getContext('2d').drawImage(img, 0, 0, lienzo.width, lienzo.height);
        lienzo.toBlob(
          function (blob) {
            if (!blob) return rej(new Error('no se pudo convertir la imagen'));
            blob.arrayBuffer().then(function (b) {
              res({ datos: new Uint8Array(b), mime: 'image/jpeg' });
            });
          },
          'image/jpeg',
          0.9
        );
      };
      img.onerror = function () { rej(new Error('no se pudo leer la obra')); };
      img.src = url;
    });
  }

  function urlBlob(bytes, tipo) {
    return URL.createObjectURL(new Blob([bytes], { type: tipo }));
  }

  function iniciar(raizEl) {
    if (raizEl.dataset.cs3dListo === '1') return;
    raizEl.dataset.cs3dListo = '1';

    var seccion = raizEl.closest('[id^="shopify-section"]') || document;
    var urlObra = raizEl.dataset.obra;
    var profundidad = parseFloat(raizEl.dataset.profundidad) || PROFUNDIDAD_M;
    /* Cuanto se nota el relieve del gel, de 0 a 200 en el editor -> 0 a 2
       aqui, que es lo que entiende `normalTexture.scale`. En 0 el lienzo sale
       liso como toda la vida y ni se fabrica el mapa. */
    var fuerzaRelieve = parseFloat(raizEl.dataset.relieve);
    if (!(fuerzaRelieve >= 0)) fuerzaRelieve = 300;
    fuerzaRelieve = fuerzaRelieve / 100;
    /* El brillo del visor (`exposure`). Estaba escrito a fuego en 1,15 y
       Gabriel pidio mas luz tres veces; a la tercera sale a deslizador, que es
       lo que habria que haber hecho a la primera. 100 = 1,0. */
    var brillo = parseFloat(raizEl.dataset.brillo);
    if (!(brillo > 0)) brillo = 150;
    brillo = brillo / 100;
    var relieve = null;
    var btnAr = raizEl.querySelector('[data-cs3d-ar]');
    var nota = raizEl.querySelector('[data-cs3d-nota]');
    if (!urlObra) return;

    /* SIN BOTÓN "VERLO EN MI PARED" (quitado el 24 sep 2026 a petición de
       Gabriel: el probador "Cuélgalo en tu casa" hace lo mismo con una foto
       de la pared de verdad). Lo único que sigue haciendo este bloque es
       decidir si se enseña el enlace del probador: se esconde en los
       modulares de varias piezas, que no tienen una sola medida, igual que
       antes se escondían los dos botones juntos. Sin el botón no se
       precarga nada: ni la obra ni el visor de realidad aumentada. */
    if (!btnAr) {
      var revisarEnlace = function () {
        raizEl.hidden = !tallaElegida(seccion);
      };
      revisarEnlace();
      ['change', 'variant:change'].forEach(function (ev) {
        seccion.addEventListener(ev, revisarEnlace);
      });
      return;
    }

    var obra = null;     // bytes de la textura, ya descargados
    var descarga = null; // promesa en curso
    var urlsVivas = [];

    function avisar(txt) {
      if (!nota) return;
      nota.textContent = txt || '';
      nota.hidden = !txt;
    }

    function precargar() {
      if (descarga) return descarga;
      /* El mapa de relieve va EN PARALELO y su fallo no rompe nada: si no se
         puede leer la obra para sacarlo (CORS, imagen rara, movil justo de
         memoria), el cuadro sale liso, que es como salia hasta hoy. Por eso
         no entra en la cadena principal con un then. */
      var pRelieve = fuerzaRelieve > 0
        ? mapaRelieve(urlObra).then(
            function (b) { relieve = b; },
            function () { relieve = null; }
          )
        : Promise.resolve();
      descarga = traerObra(urlObra)
        .then(function (r) { obra = r; return pRelieve.then(function () { return r; }); })
        .catch(function (e) { descarga = null; throw e; });
      return descarga;
    }

    // Se precarga al primer roce, para que al pulsar ya este todo listo.
    ['pointerenter', 'touchstart', 'focusin'].forEach(function (ev) {
      raizEl.addEventListener(
        ev,
        function () {
          precargar().catch(function () {});
          if (esAndroid()) asegurarAr().catch(function () {});
        },
        { once: true, passive: true }
      );
    });

    function medidas() {
      var t = tallaElegida(seccion);
      if (!t) return null;
      return { ancho: t.ancho, alto: t.alto, fondo: profundidad };
    }

    /* Los modulares de varias piezas no dan medidas de una sola caja: ahi se
       esconden los botones en vez de ensenar algo que engane. */
    function revisarDisponibilidad() {
      var m = medidas();
      raizEl.hidden = !m;
      return m;
    }

    function limpiarUrls() {
      urlsVivas.forEach(function (u) { URL.revokeObjectURL(u); });
      urlsVivas = [];
    }

    function glbBlob(m) {
      var bytes = construirGLB(
        m.ancho, m.alto, m.fondo, obra.datos, obra.mime, relieve, fuerzaRelieve);
      var u = urlBlob(bytes, 'model/gltf-binary');
      urlsVivas.push(u);
      return u;
    }

    function usdzBlob(m) {
      var bytes = construirUSDZ(m.ancho, m.alto, m.fondo, obra.datos, obra.mime);
      var u = urlBlob(bytes, 'model/vnd.usdz+zip');
      urlsVivas.push(u);
      return u;
    }

    /* --- AR de Android: dejarlo listo ANTES de que lo pulsen --------------

       WebXR exige que la sesion de AR arranque desde un gesto real del
       usuario. Si al pulsar nos ponemos a cargar model-viewer, el gesto
       caduca y no pasa nada. Por eso se mantiene un model-viewer oculto con
       el modelo de la talla actual ya cargado: al pulsar solo hay que llamar
       a activateAR(), sin un solo await de por medio. */
    var mvAr = null;
    var prepararAr = null;

    function asegurarAr() {
      if (prepararAr) return prepararAr;
      prepararAr = precargar()
        .then(cargarModelViewer)
        .then(function () {
          if (!mvAr) {
            mvAr = document.createElement('model-viewer');
            mvAr.setAttribute('ar', '');
            /* Solo WebXR. Scene Viewer es otra app y no puede leer un blob:
               del navegador, asi que anadirlo aqui haria que model-viewer lo
               prefiriese y fallara en silencio. */
            mvAr.setAttribute('ar-modes', 'webxr');
            mvAr.setAttribute('ar-placement', 'wall');
            mvAr.setAttribute('ar-scale', 'fixed');
            mvAr.setAttribute('alt', 'Cuadro para ver en tu pared');
            mvAr.style.cssText =
              'position:absolute;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none';
            raizEl.appendChild(mvAr);
          }
          return sincronizarAr();
        });
      return prepararAr;
    }

    function sincronizarAr() {
      var m = medidas();
      if (!mvAr || !obra || !m) return Promise.resolve();
      mvAr.setAttribute('src', glbBlob(m));
      return new Promise(function (res) {
        if (mvAr.loaded) return res();
        mvAr.addEventListener('load', function () { res(); }, { once: true });
        setTimeout(res, 4000); // no dejar la promesa colgada nunca
      });
    }

    /* --- Ventana del visor 3D --- */
    var capa = null;

    function cerrarVisor() {
      if (!capa) return;
      capa.classList.remove('is-abierta');
      capa.querySelector('.cs3d-capa__visor').innerHTML = '';
      document.body.style.overflow = '';
      limpiarUrls();
    }

    function abrirVisor(m, conAr) {
      if (!capa) {
        capa = document.createElement('div');
        capa.className = 'cs3d-capa';
        capa.innerHTML =
          '<div class="cs3d-capa__caja" role="dialog" aria-modal="true" aria-label="Vista en 3D del cuadro">' +
          '<button type="button" class="cs3d-capa__cerrar" aria-label="Cerrar">&#215;</button>' +
          '<div class="cs3d-capa__visor"></div>' +
          '<p class="cs3d-capa__pie"></p>' +
          '</div>';
        document.body.appendChild(capa);
        capa.addEventListener('click', function (e) {
          if (e.target === capa || e.target.closest('.cs3d-capa__cerrar')) cerrarVisor();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && capa && capa.classList.contains('is-abierta')) cerrarVisor();
        });
      }

      var visor = capa.querySelector('.cs3d-capa__visor');
      var pie = capa.querySelector('.cs3d-capa__pie');
      visor.innerHTML = '<p class="cs3d-capa__cargando">Preparando el cuadro en 3D...</p>';
      pie.textContent =
        Math.round(m.ancho * 100) + ' × ' + Math.round(m.alto * 100) + ' cm, a tamaño real';
      capa.classList.add('is-abierta');
      document.body.style.overflow = 'hidden';

      precargar()
        .then(function () { return cargarModelViewer(); })
        .then(function () {
          var mv = document.createElement('model-viewer');
          mv.setAttribute('src', glbBlob(m));
          mv.setAttribute('alt', 'Vista en tres dimensiones del cuadro');
          mv.setAttribute('camera-controls', '');
          mv.setAttribute('touch-action', 'pan-y');
          /* Luz de sala de museo en vez del 'neutral' de fabrica, que reparte
             la misma luz por todas partes y deja el lienzo sin relieve. La
             sombra sube a 1.4 y el foco viene de arriba: asi el bastidor
             proyecta su sombra y el hueco del dorso se lee como un hueco. */
          /* FIDELIDAD DE COLOR: `neutral`, no el `auto` de fabrica.
             Sin este atributo model-viewer usa `auto`, que aplica una curva
             de cine (ACES/AgX): oscurece los medios tonos y DESATURA los
             colores vivos. Para una peli esta bien; para una tienda que vende
             REPRODUCCIONES es mentir sobre el producto — el cliente compara
             lo que recibe con lo que vio en pantalla. Gabriel lo describio
             como "la luz se ve oscura y cambia el color del cuadro"
             (19 sep 2026).
             `neutral` es la curva PBR Neutral de Khronos, hecha justo para
             catalogo: conserva el tono y la saturacion y deja el blanco
             blanco. La alternativa es `commerce`, un pelin mas contrastada.
             Si algun dia se quiere volver al look de cine, es este atributo
             y solo este. */
          mv.setAttribute('tone-mapping', 'neutral');
          mv.setAttribute('environment-image', entornoSala());
          mv.setAttribute('shadow-intensity', '1.4');
          mv.setAttribute('shadow-softness', '0.7');
          mv.setAttribute('exposure', String(brillo));
          mv.setAttribute('camera-orbit', '-28deg 74deg 105%');
          mv.setAttribute('min-field-of-view', '12deg');
          mv.setAttribute('interaction-prompt', 'none');
          if (conAr && !esIOS()) {
            /* Solo WebXR. Scene Viewer es una app aparte y Android no le puede
               pasar un blob: del navegador, asi que con el modelo generado al
               vuelo no sirve: fallaria en silencio. */
            mv.setAttribute('ar', '');
            mv.setAttribute('ar-modes', 'webxr');
            mv.setAttribute('ar-placement', 'wall');
            mv.setAttribute('ar-scale', 'fixed');
          }
          visor.innerHTML = '';
          visor.appendChild(mv);
        })
        .catch(function (e) {
          visor.innerHTML =
            '<p class="cs3d-capa__cargando">No se ha podido cargar la vista en 3D.</p>';
          if (window.console) console.warn('[canvas-3d]', e);
        });
    }

    /* --- Boton "verlo en mi pared" --- */
    btnAr.addEventListener('click', function () {
        var m = medidas();
        if (!m) return avisar('Elige primero un tamaño.');

        if (esIOS()) {
          /* Todo sincrono a partir de aqui: si metieramos un await, el gesto
             del usuario caducaria y Quick Look no llegaria a abrirse. */
          if (!obra) {
            avisar('Un momento, preparando la vista...');
            return precargar()
              .then(function () { avisar('Ya esta: vuelve a pulsar para verlo en tu pared.'); })
              .catch(function () { avisar('No se ha podido preparar la vista en 3D.'); });
          }
          avisar('');
          var enlace = document.createElement('a');
          enlace.setAttribute('rel', 'ar');
          /* allowsContentScaling=0 impide que el usuario reescale el cuadro
             dentro de Quick Look: sin esto, la escala real deja de estarlo. */
          enlace.href = usdzBlob(m) + '#allowsContentScaling=0';
          enlace.appendChild(document.createElement('img')); // Quick Look lo exige
          document.body.appendChild(enlace);
          enlace.click();
          setTimeout(function () { enlace.remove(); }, 1000);
          return;
        }

        if (esAndroid()) {
          if (mvAr && mvAr.loaded) {
            if (mvAr.canActivateAR) {
              avisar('');
              try {
                mvAr.activateAR();
              } catch (e) {
                avisar('No se ha podido abrir la camara. Prueba a abrir esta pagina en Chrome.');
                if (window.console) console.warn('[canvas-3d] activateAR', e);
              }
              return;
            }
            /* El movil no puede hacer realidad aumentada en el navegador. Lo
               habitual: no es Chrome, o falta "Servicios de Google Play para
               RA". Se dice con claridad en vez de dejarlo en un 3D mudo. */
            avisar(
              'Tu navegador no permite ver el cuadro en tu pared. Abre esta pagina en Chrome y comprueba que tienes instalado "Servicios de Google Play para RA". Mientras tanto, aqui lo tienes en 3D.'
            );
            abrirVisor(m, false);
            return;
          }

          avisar('Un momento, preparando la vista...');
          asegurarAr()
            .then(function () {
              avisar(
                mvAr && mvAr.canActivateAR
                  ? 'Ya esta: vuelve a pulsar para verlo en tu pared.'
                  : 'Tu navegador no permite ver el cuadro en tu pared. Abre esta pagina en Chrome y comprueba que tienes instalado "Servicios de Google Play para RA".'
              );
            })
            .catch(function () { avisar('No se ha podido preparar la vista en 3D.'); });
          return;
        }

        avisar('Para verlo en tu pared hace falta el movil. Abre esta pagina en el telefono; aqui puedes verlo en 3D.');
        abrirVisor(m, false);
      });

    revisarDisponibilidad();
    ['change', 'variant:change'].forEach(function (ev) {
      seccion.addEventListener(ev, function () {
        revisarDisponibilidad();
        avisar('');
        // El modelo del AR es de la talla anterior: hay que rehacerlo, o el
        // cuadro apareceria en la pared con el tamano equivocado.
        if (mvAr) sincronizarAr();
      });
    });
  }

  function arrancar() {
    document.querySelectorAll('[data-cs3d]').forEach(iniciar);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', arrancar);
    } else {
      arrancar();
    }
    document.addEventListener('shopify:section:load', arrancar);
  }

  /* ======================================================================
     6 * API publica
     ====================================================================== */

  var API = {
    PROFUNDIDAD_M: PROFUNDIDAD_M,
    medirTalla: medirTalla,
    geometria: geometria,
    texturaBastidor: texturaBastidor,
    entornoSala: entornoSala,
    construirGLB: construirGLB,
    construirUSDA: construirUSDA,
    construirUSDZ: construirUSDZ,
    empaquetarUSDZ: empaquetarUSDZ,
    crc32: crc32
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = API; // para poder probarlo en Node
  } else {
    raiz.CuadrosSplash3D = API;
  }
})(typeof self !== 'undefined' ? self : this);
